import type { EntityId, IChartWidgetApi, IChartingLibraryWidget } from "@ekidenfi/tradingview-chart";

import type { ChartTpSlPreview } from "@stores/TradeboxStore";

export type ChartTpSlOverlayIds = {
  riskReward: EntityId | null;
  extras: EntityId[];
};

/**
 * Long/short position tool: no permanent price-scale chips.
 * Details typically appear when the user hovers or selects the drawing (library default).
 */
const RR_LONG_OVERRIDES = {
  "linetoolriskrewardlong.alwaysShowStats": false,
  "linetoolriskrewardlong.showPriceLabels": false,
  "linetoolriskrewardlong.compact": true,
} as const;

const RR_SHORT_OVERRIDES = {
  "linetoolriskrewardshort.alwaysShowStats": false,
  "linetoolriskrewardshort.showPriceLabels": false,
  "linetoolriskrewardshort.compact": true,
} as const;

const HORIZ_LINE_OVERRIDES_TP = {
  "linetoolhorzline.linecolor": "#089981",
  "linetoolhorzline.linewidth": 1,
  "linetoolhorzline.showLabel": false,
} as const;

const HORIZ_LINE_OVERRIDES_SL = {
  "linetoolhorzline.linecolor": "#f23645",
  "linetoolhorzline.linewidth": 1,
  "linetoolhorzline.showLabel": false,
} as const;

/**
 * `symbolInfo.pointvalue` in TV risk/reward (`_ownerSourcePointValue()`), else 1.
 */
function getChartPointValue(chart: IChartWidgetApi): number {
  try {
    const ext = chart.symbolExt() as { pointvalue?: number } | null;
    const pv = ext?.pointvalue;
    if (pv != null && Number.isFinite(pv) && pv > 0) return pv;
  } catch {
    /* noop */
  }
  return 1;
}

/** `getProperties()` may return numbers, strings, or wrapped TV property payloads. */
function pickNumeric(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  if (v != null && typeof v === "object") {
    const o = v as Record<string, unknown>;
    const inner = o.v ?? o.value;
    if (typeof inner === "number" && Number.isFinite(inner)) return inner;
    if (typeof inner === "string" && inner.trim() !== "") {
      const n = Number(inner);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/**
 * After stop/target are applied, TV rounds prices to the symbol tick grid. We must use those
 * levels for money risk so `qty = riskSize / (|entry − stop| × pointValue)` matches `baseQty`.
 */
function syncRiskRewardMoneyFromShape(
  chart: IChartWidgetApi,
  shapeId: EntityId,
  baseQty: number,
  pointValue: number,
  fallbackPrices?: { entry: number; stop: number },
) {
  const api = chart.getShapeById(shapeId);
  if (!api || !Number.isFinite(baseQty) || baseQty <= 0) return;

  let props: Record<string, unknown>;
  try {
    props = api.getProperties() as Record<string, unknown>;
  } catch {
    return;
  }

  let entry = pickNumeric(props.entryPrice);
  let stop = pickNumeric(props.stopPrice);
  if (entry == null || stop == null) {
    if (fallbackPrices == null) return;
    entry = fallbackPrices.entry;
    stop = fallbackPrices.stop;
  }

  const priceSpan = Math.abs(entry - stop);
  if (!(priceSpan > 0)) return;

  const riskMoney = baseQty * priceSpan * pointValue;
  const notionalQuote = baseQty * entry;
  const accountSize = Math.max(riskMoney, notionalQuote);

  try {
    api.setProperties({
      riskDisplayMode: "money",
      accountSize,
      lotSize: 1,
      risk: riskMoney,
      riskSize: riskMoney,
      qty: baseQty,
    });
  } catch {
    /* noop */
  }
}

function scheduleRiskRewardMoneySync(
  chart: IChartWidgetApi,
  shapeId: EntityId,
  baseQty: number,
  pointValue: number,
  fallbackPrices?: { entry: number; stop: number },
) {
  const run = () => syncRiskRewardMoneyFromShape(chart, shapeId, baseQty, pointValue, fallbackPrices);
  queueMicrotask(run);
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}

export function clearTpSlOverlays(widget: IChartingLibraryWidget | null, state: ChartTpSlOverlayIds) {
  if (!widget) return;
  let chart: IChartWidgetApi;
  try {
    chart = widget.activeChart();
  } catch {
    return;
  }
  if (state.riskReward !== null) {
    try {
      chart.removeEntity(state.riskReward);
    } catch {
      /* noop */
    }
    state.riskReward = null;
  }
  for (const id of state.extras) {
    try {
      chart.removeEntity(id);
    } catch {
      /* noop */
    }
  }
  state.extras.length = 0;
}

function applyTpSlOverlaysOnReady(
  chart: IChartWidgetApi,
  preview: ChartTpSlPreview | null,
  state: ChartTpSlOverlayIds,
) {
  clearTpSlOverlaysByChart(chart, state);

  if (!preview) return;

  const vr = chart.getVisibleRange();
  if (!vr) return;

  const { entry, takeProfit, stopLoss, direction, baseSize } = preview;
  if (!Number.isFinite(entry) || entry <= 0) return;

  const hasTp = takeProfit != null && Number.isFinite(takeProfit);
  const hasSl = stopLoss != null && Number.isFinite(stopLoss);
  if (!hasTp && !hasSl) return;

  const shape = direction === "long" ? "long_position" : "short_position";
  const anchorTime = vr.to;
  const span = Math.max(vr.to - vr.from, 60);
  const t0 = vr.to - span * 0.18;
  const t1 = vr.to;

  if (hasTp && hasSl) {
    const id = chart.createMultipointShape(
      [
        { time: t0, price: entry },
        { time: t1, price: entry },
      ],
      {
        shape,
        lock: false,
        disableSave: true,
        zOrder: "top",
        overrides: direction === "long" ? { ...RR_LONG_OVERRIDES } : { ...RR_SHORT_OVERRIDES },
      },
    );

    if (id) {
      state.riskReward = id;
      try {
        const api = chart.getShapeById(id);
        const prev = api.getProperties();
        const rrLayout = direction === "long" ? RR_LONG_OVERRIDES : RR_SHORT_OVERRIDES;
        const pointValue = getChartPointValue(chart);

        try {
          api.setProperties({
            ...prev,
            ...rrLayout,
            stopPrice: stopLoss!,
            targetPrice: takeProfit!,
          });
        } catch {
          try {
            api.setProperties({
              ...prev,
              stopPrice: stopLoss!,
              targetPrice: takeProfit!,
            });
          } catch {
            /* noop */
          }
        }

        if (baseSize != null && Number.isFinite(baseSize) && baseSize > 0 && hasSl && stopLoss != null) {
          const priceSpanPreview = direction === "long" ? entry - stopLoss : stopLoss - entry;
          if (Number.isFinite(priceSpanPreview) && priceSpanPreview > 0) {
            scheduleRiskRewardMoneySync(chart, id, baseSize, pointValue, { entry, stop: stopLoss! });
          }
        }
      } catch {
        /* setProperties shape may differ by TV build */
      }
    }

    return;
  }

  if (hasTp) {
    const tid = chart.createShape(
      { time: anchorTime, price: takeProfit! },
      {
        shape: "horizontal_line",
        lock: true,
        disableSave: true,
        disableSelection: true,
        zOrder: "top",
        overrides: { ...HORIZ_LINE_OVERRIDES_TP },
      },
    );
    if (tid) state.extras.push(tid);
  }

  if (hasSl) {
    const sid = chart.createShape(
      { time: anchorTime, price: stopLoss! },
      {
        shape: "horizontal_line",
        lock: true,
        disableSave: true,
        disableSelection: true,
        zOrder: "top",
        overrides: { ...HORIZ_LINE_OVERRIDES_SL },
      },
    );
    if (sid) state.extras.push(sid);
  }
}

function clearTpSlOverlaysByChart(chart: IChartWidgetApi, state: ChartTpSlOverlayIds) {
  if (state.riskReward !== null) {
    try {
      chart.removeEntity(state.riskReward);
    } catch {
      /* noop */
    }
    state.riskReward = null;
  }
  for (const id of state.extras) {
    try {
      chart.removeEntity(id);
    } catch {
      /* noop */
    }
  }
  state.extras.length = 0;
}

/**
 * TP/SL overlay: risk/reward zones or plain lines only — no static comment/axis badges.
 */
export function applyTpSlOverlays(
  widget: IChartingLibraryWidget,
  preview: ChartTpSlPreview | null,
  state: ChartTpSlOverlayIds,
) {
  let chart: IChartWidgetApi;
  try {
    chart = widget.activeChart();
  } catch {
    return;
  }

  chart.dataReady(() => {
    try {
      applyTpSlOverlaysOnReady(chart, preview, state);
    } catch (e) {
      console.warn("[TradingView TP/SL overlay]", e);
    }
  });
}
